import { AsyncPipe } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, Input, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { refreshCredentials } from 'app/core/auth/auth.actions';
import { ResourceCredentials } from 'app/core/auth/user.model';
import { Response, TasksService } from 'app/shared/backend';
import { LabelForDirective } from 'app/shared/form-utils/label-for.directive';
import { IsInvalidControlDirective } from 'app/shared/form-utils/is-invalid.directive';
import { FormAttributeDirective } from 'app/shared/form-utils/form-attribute.directive';
import { AutofocusDirective } from 'app/shared/form-utils/autofocus.directive';
import { AutocompleteOffFormDirective, AutocompleteOffInputDirective } from 'app/shared/form-utils/autocomplete-off.directive';
import { Credentials } from 'app/shared/service/credentials.model';
import { MyAutopropApi } from 'app/shared/service/my-autoprop-api.service';
import { SupportService } from 'app/shared/support/support.service';
import { ModalsService } from 'app/shared/ui/modals/modals.service';
import { OverlayRef } from 'app/shared/ui/overlays';
import { BehaviorSubject, Subject } from 'rxjs';
import { finalize, map, mergeMap, takeUntil } from 'rxjs/operators';
import { UiModule } from '../ui';
import { PipesModule } from '../pipes';


@Component({
    selector: 'datasource-credentials-modal',
    imports: [
        UiModule,
        PipesModule,
        AutocompleteOffFormDirective,
        AutocompleteOffInputDirective,
        AutofocusDirective,
        FormAttributeDirective,
        IsInvalidControlDirective,
        LabelForDirective,
        FormsModule,
        ReactiveFormsModule,
        AsyncPipe,
    ],
    styles: [
        `
        .conceal {
            font-family: 'text-security-disc';
        }
    `,
    ],
    template: `
        <ui-modal>
            <modal-header>
                @if (credentialsLoadingInProgress) {
                    <span>Loading Your Credentials...</span>
                }
                @else if (credentials) {
                    <span>{{ credentials.form.display_name }}</span>
                }
            </modal-header>
            <div class="modal-toolbar bg-icon form-inline"></div>
            <modal-body>
                @if (credentialsLoadingInProgress) {
                    <ui-block-loading></ui-block-loading>
                }
                @else if (credentials) {
                    <div>
                        <form
                            id="changePasswordForm"
                            class="row justify-content-center"
                            (ngSubmit)="verifyCredentials()"
                            [formGroup]="form"
                            novalidate
                            autocomplete="off">
                            <div class="form-group col-8">
                                <label class="form-control-label" for="username">Username</label>
                                <input
                                    class="form-control"
                                    id="username"
                                    autofocus
                                    formControlName="username"
                                    [attr.disabled]="credentialsChecked && credentialsValid ? '' : null">
                            </div>
                            <div class="form-group col-8">
                                <label class="form-control-label" for="password">Password</label>
                                <div class="input-group">
                                    <input
                                        class="form-control"
                                        id="password"
                                        [attr.disabled]="credentialsChecked && credentialsValid ? '' : null"
                                        (focus)="clearPassword()"
                                        [class.conceal]="concealPassword"
                                        formControlName="password">
                                    <div class="input-group-append">
                                        @if (isPasswordChanged()) {
                                            <span
                                                tooltip="Click to show / hide password"
                                                class="input-group-text cursor-pointer"
                                                (click)="concealPassword = !concealPassword">
                                                <i class="fa" [class.fa-eye]="concealPassword" [class.fa-eye-slash]="!concealPassword"></i>
                                            </span>
                                        }
                                    </div>
                                </div>
                            </div>
                        </form>
                        @if (credentialsChecked && !credentialsValid) {
                            <ui-alert type="danger">
                                <div [innerHTML]="message"></div>
                            </ui-alert>
                        }
                        @if (credentialsChecked && credentialsValid) {
                            <ui-alert type="success">
                                Your credentials are checked and valid. You can safely save them.
                            </ui-alert>
                        }
                    </div>
                }
            </modal-body>
            <modal-footer>
                <div class="w-100">
                    <div class="row">
                        <div class="col-2">
                            <button class="btn btn-secondary mx-1" (click)="cancel()" [disabled]="verifyInProgress | async | default: false">Cancel</button>
                        </div>
                        <div class="col-10">
                            @if (!credentialsValid) {
                                <button
                                    type="submit"
                                    class="btn btn-info verify-credentials pull-right mx-1"
                                    [disabled]="credentialsLoadingInProgress"
                                    [loading]="verifyInProgress | async | default: true"
                                    form="changePasswordForm">
                                    <i class="fa fa-refresh"></i>
                                    Verify Credentials
                                </button>
                            }
                            @else {
                                <button
                                    class="btn btn-success save-credentials pull-right mx-1"
                                    (click)="updateCredentials()"
                                    autofocus>
                                    <i class="fa fa-save"></i>
                                    Save Credentials
                                </button>
                            }
                            <button class="btn btn-danger pull-right mx-1" (click)="removeCredentials()">
                                <i class="fa fa-times"></i>
                                Remove Credentials
                            </button>
                        </div>
                    </div>
                </div>
            </modal-footer>
        </ui-modal>
    `,
})
export class DatasourceCredentialsModalComponent implements OnDestroy {

    @Input('name')
    set name(name: ResourceCredentials) {
        this.myAutopropApi.getCredentials()
            .subscribe({
                next: res => {
                    const credentials = res.payload.credentials.find(i => Array.isArray(name) ? name.includes(i.name) : i.name === name);
                    if (credentials) {
                        this.credentialsLoadingInProgress = false;
                        this._credentials = credentials;
                        this.setFormData(credentials);
                    }
                },
                error: () => this.overlayRef.dismiss('Error retrieving credentials')
            });
    }

    @Input()
    set credentials(credentials: Credentials) {
        if (credentials) {
            this.credentialsLoadingInProgress = false;
            this._credentials = credentials;
            this.setFormData(credentials);
        }
    }

    get credentials() {
        return this._credentials;
    }

    verifyInProgress = new BehaviorSubject(false);

    credentialsLoadingInProgress = true;

    credentialsValid = false;

    credentialsChecked = false;

    concealPassword = true;

    message: string | null = null;

    private _credentials: Credentials;

    form: FormGroup;

    supportListener = false;

    destroyed = new Subject<void>();

    constructor(
        private overlayRef: OverlayRef,
        private _fb: FormBuilder,
        private tasksService: TasksService,
        private modalsService: ModalsService,
        private myAutopropApi: MyAutopropApi,
        private cdr: ChangeDetectorRef,
        private store: Store,
        private supportService: SupportService,
        private elementRef: ElementRef
    ) {
        this.overlayRef.registerDismissGuard(() => this.verifyInProgress.pipe(map(x => !x)));
    }

    setFormData(credentials: Credentials) {
        this.form = this._fb.group({
            name: credentials.name,
            username: credentials.data.username,
            password: credentials.data.password,
        });
        this.cdr.detectChanges();
    }

    private processCredentialsResponse(response: Response) {
        this.store.dispatch(refreshCredentials());
        this.overlayRef.close(response.payload.credentialsFormData);
    }

    updateCredentials() {
        this.myAutopropApi.updateCredentials(this.form.value)
            .pipe(takeUntil(this.destroyed))
            .subscribe(res => {
                this.modalsService.alert(
                    'Credentials updated successfully. Changes will take effect immediately.',
                    'Notification',
                    'info'
                );
                this.processCredentialsResponse(res);
            });
    }

    removeCredentials() {
        this.modalsService.confirm('Are you sure you want to remove your credentials?', 'Question', 'danger').result
            .pipe(
                mergeMap(_ => this.myAutopropApi.removeCredentials(this.credentials.name)),
                takeUntil(this.destroyed)
            )
            .subscribe(res => {
                this.modalsService.alert(
                    'Credentials removed successfully. Changes will take effect immediately.',
                    'Notification',
                    'info'
                );
                this.processCredentialsResponse(res);
            });
    }

    isPasswordChanged() {
        return this.form.controls['password'].dirty;
    }

    clearPassword() {
        if (this.isPasswordChanged()) {
            return;
        }
        this.form.patchValue({ password: null });
        this.form.controls['password'].markAsDirty();
        this.form.markAsDirty();
        this.cdr.detectChanges();
    }

    verifyCredentials() {
        this.verifyInProgress.next(true);
        this.tasksService.createTasks('/api/check-credentials', this.form.value).pipe(
            mergeMap(r => r.tasks[0].result),
            finalize(() => {
                this.verifyInProgress.next(false);
                this.credentialsChecked = true;
                this.cdr.detectChanges();
            }),
            takeUntil(this.destroyed)
        ).subscribe({
            next: result => {
                this.credentialsValid = result.credentialsValid;
                this.message = result.message;
                this.cdr.detectChanges();
            },
            error: () => {
                this.credentialsValid = false;
                this.message = 'Ooops, we were unable to verify your credentials. Please, try again later.';
                this.cdr.detectChanges();
            }
        });
    }

    openSupport() {
        this.supportService.openSupport();
    }

    ngAfterViewChecked() {
    if (this.elementRef.nativeElement.querySelector('a[muted="open-support"]')) {
        this.elementRef.nativeElement
            .querySelector('a[muted="open-support"]')
            .addEventListener(
                'click',
                this.openSupport.bind(this)
            );
        this.supportListener = true;
    }
  }

    cancel() {
        this.overlayRef.dismiss();
    }

    ngOnDestroy() {
        if (this.supportListener) {
            this.elementRef.nativeElement.querySelector('a[muted="open-support"]')?.removeEventListener('click', this.openSupport.bind(this));
        }
        this.destroyed.next();
        this.destroyed.complete();
    }
}
